import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Shell from "../components/Shell";
import api, { formatApiError } from "../lib/api";

const BRAND_OPTIONS = [
    "Jalupro", "Promoitalia", "Aqualyx", "Alidya", "JBP", "DQ Paris",
    "Meta Cell Technology", "Endolift", "Icoone", "Biodermogenesi",
];

export default function Register() {
    const navigate = useNavigate();
    const [form, setForm] = useState({
        clinic_name: "", trading_name: "", nzbn: "", website: "", instagram: "",
        address: "", phone: "", contact_name: "", position: "", email: "",
        mobile: "", professional_type: "", registration_number: "", notes: "",
        password: "", confirm: "",
    });
    const [brands, setBrands] = useState([]);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => { window.scrollTo(0, 0); }, []);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const toggleBrand = (b) => setBrands((bs) => bs.includes(b) ? bs.filter(x => x !== b) : [...bs, b]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (form.password.length < 8) return setError("Password must be at least 8 characters");
        if (form.password !== form.confirm) return setError("Passwords do not match");
        setLoading(true);
        try {
            const payload = { ...form, brand_interests: brands };
            delete payload.confirm;
            await api.post("/auth/register", payload);
            setSuccess(true);
            window.scrollTo(0, 0);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <Shell variant="public">
                <div className="max-w-2xl mx-auto px-6 py-24 text-center animate-fade-in">
                    <p className="eyebrow mb-3">Application received</p>
                    <h1 className="font-heading text-4xl font-light tracking-tight mb-5">Thank you for applying</h1>
                    <p className="text-zinc-600 text-base leading-relaxed mb-10 max-w-lg mx-auto">
                        Your registration for <strong>{form.clinic_name}</strong> has been received. Our team will manually review your application within 1–2 business days. We will notify you by email at <strong>{form.email}</strong> once your account has been approved.
                    </p>
                    <div className="flex items-center justify-center gap-4">
                        <Link to="/login" data-testid="success-back-login" className="btn-secondary">Back to sign in</Link>
                        <button onClick={() => navigate("/contact")} className="btn-ghost">Contact Dermalis</button>
                    </div>
                </div>
            </Shell>
        );
    }

    return (
        <Shell variant="public">
            <div className="max-w-3xl mx-auto px-6 py-12 lg:py-16 animate-fade-in">
                <p className="eyebrow mb-3">Clinic application</p>
                <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tight mb-3">Register your clinic</h1>
                <p className="text-zinc-600 text-base leading-relaxed mb-12 max-w-xl">
                    Dermalis NZ is a private wholesale portal for licensed healthcare and aesthetic clinics in New Zealand. Submit your application below — manual approval typically takes 1–2 business days.
                </p>

                <form onSubmit={onSubmit} className="space-y-12" data-testid="register-form">
                    <Section title="Clinic details">
                        <Field label="Clinic name *"><input className="input-base" required value={form.clinic_name} onChange={set("clinic_name")} data-testid="reg-clinic-name" /></Field>
                        <Field label="Trading name"><input className="input-base" value={form.trading_name} onChange={set("trading_name")} data-testid="reg-trading-name" /></Field>
                        <Field label="NZBN"><input className="input-base" value={form.nzbn} onChange={set("nzbn")} data-testid="reg-nzbn" /></Field>
                        <Field label="Website"><input className="input-base" value={form.website} onChange={set("website")} data-testid="reg-website" /></Field>
                        <Field label="Instagram"><input className="input-base" value={form.instagram} onChange={set("instagram")} data-testid="reg-instagram" /></Field>
                        <Field label="Clinic phone *"><input className="input-base" required value={form.phone} onChange={set("phone")} data-testid="reg-phone" /></Field>
                        <Field label="Address *" span2><input className="input-base" required value={form.address} onChange={set("address")} data-testid="reg-address" /></Field>
                    </Section>

                    <Section title="Main contact">
                        <Field label="Contact name *"><input className="input-base" required value={form.contact_name} onChange={set("contact_name")} data-testid="reg-contact-name" /></Field>
                        <Field label="Position"><input className="input-base" value={form.position} onChange={set("position")} data-testid="reg-position" /></Field>
                        <Field label="Email *"><input className="input-base" type="email" required value={form.email} onChange={set("email")} data-testid="reg-email" /></Field>
                        <Field label="Mobile"><input className="input-base" value={form.mobile} onChange={set("mobile")} data-testid="reg-mobile" /></Field>
                    </Section>

                    <Section title="Professional registration">
                        <Field label="Professional type"><input className="input-base" placeholder="e.g. Registered Nurse, Doctor" value={form.professional_type} onChange={set("professional_type")} data-testid="reg-prof-type" /></Field>
                        <Field label="Registration number"><input className="input-base" value={form.registration_number} onChange={set("registration_number")} data-testid="reg-reg-number" /></Field>
                    </Section>

                    <Section title="Brand interests">
                        <div className="col-span-2 flex flex-wrap gap-2">
                            {BRAND_OPTIONS.map((b) => (
                                <button type="button" key={b} data-testid={`reg-brand-${b.toLowerCase().replace(/\s+/g, '-')}`} onClick={() => toggleBrand(b)} className={`text-xs uppercase tracking-widest px-3 py-2 border rounded-sm transition-colors ${brands.includes(b) ? "bg-black text-white border-black" : "bg-white text-zinc-600 border-zinc-200 hover:border-black"}`}>{b}</button>
                            ))}
                        </div>
                    </Section>

                    <Section title="Account password">
                        <Field label="Password (min 8 chars) *"><input className="input-base" type="password" required value={form.password} onChange={set("password")} data-testid="reg-password" /></Field>
                        <Field label="Confirm password *"><input className="input-base" type="password" required value={form.confirm} onChange={set("confirm")} data-testid="reg-confirm" /></Field>
                    </Section>

                    <Section title="Notes">
                        <div className="col-span-2">
                            <textarea className="input-base min-h-[100px]" rows={4} value={form.notes} onChange={set("notes")} placeholder="Anything you'd like us to know" data-testid="reg-notes" />
                        </div>
                    </Section>

                    {error && <div data-testid="register-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-2.5 rounded-sm">{error}</div>}

                    <div className="flex items-center justify-between pt-6 border-t border-zinc-200">
                        <Link to="/login" className="text-sm text-zinc-500 hover:text-black">← Back to sign in</Link>
                        <button data-testid="register-submit" type="submit" disabled={loading} className="btn-primary">{loading ? "Submitting…" : "Submit application"}</button>
                    </div>
                </form>
            </div>
        </Shell>
    );
}

function Section({ title, children }) {
    return (
        <section>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div>
                    <p className="eyebrow">{title}</p>
                </div>
                <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {children}
                </div>
            </div>
        </section>
    );
}

function Field({ label, span2, children }) {
    return (
        <div className={span2 ? "sm:col-span-2" : ""}>
            <label className="input-label">{label}</label>
            {children}
        </div>
    );
}
